import { useState } from 'react';
import {
  Archive,
  BadgeDollarSign,
  Pencil,
  Play,
  Pause,
  CheckCircle2,
  FileText,
  Lock,
  XCircle,
  Link as LinkIcon,
  Loader2,
  CalendarDays,
  FilePlus2,
  MessageSquareText,
  Receipt,
} from 'lucide-react';
import { useRouter } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/atoms/dropdown-menu';
import { OverflowMenuContent, RowOverflowMenuTrigger } from '@/components/molecules';
import { useApiMutation } from '@/hooks/useApiMutation';
import { apiPost, operationsClient } from '@/lib/api';
import { jobEndpoints } from '@/lib/api/endpoints';
import { jobKeys } from '@/lib/queryKeys';
import {
  hasJobLineItems,
  START_JOB_REQUIRES_LINE_ITEMS_MESSAGE,
} from '@/lib/jobs/jobScopeValidation';
import { DEFAULT_ESTIMATE_LIST_FILTERS } from '@/components/organisms/estimates/support/estimate-list-filters';
import { useCreateJobApprovalEstimate } from '@/hooks/jobs/useJobs';
import { useHasAnyPermission, useHasPermission, usePermissionScope } from '@/hooks/usePermissions';
import type { Job } from '@/types/job';

const DEFAULT_ESTIMATE_DETAIL_SEARCH = {
  ...DEFAULT_ESTIMATE_LIST_FILTERS,
  tab: 'overview' as const,
};

const ACTIVE_JOB_STATUSES = new Set(['DRAFT', 'SCHEDULED', 'IN_PROGRESS', 'ON_HOLD']);
const ARCHIVABLE_JOB_STATUSES = new Set(['COMPLETED', 'INVOICED']);

interface JobRowMenuProps {
  id: string;
  status: string;
  statusPhase?: string | null;
  estimateId?: string | null;
  source?: Job['source'] | null;
  bookingType?: Job['bookingType'];
  recurringStatus?: Job['recurringStatus'];
  lineItems?: Job['lineItems'];
}

export function JobRowMenu({
  id,
  status,
  statusPhase,
  estimateId,
  bookingType,
  lineItems = [],
}: JobRowMenuProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const effectiveStatus = statusPhase ?? status;
  const isReadOnly =
    effectiveStatus === 'CANCELLED' ||
    effectiveStatus === 'CLOSED' ||
    effectiveStatus === 'ARCHIVED';
  const canTransition = !isReadOnly;
  const canEditJob = ACTIVE_JOB_STATUSES.has(effectiveStatus);
  const hasCreateVisitPermission = useHasAnyPermission('jobs:edit', 'jobs:create_visit');
  // List rows do not carry their visit crew. Scoped editors can use the job
  // detail page, where the lead assignment is available to check.
  const jobEditScope = usePermissionScope('jobs:edit');
  const canCreateVisit = hasCreateVisitPermission && ACTIVE_JOB_STATUSES.has(effectiveStatus);
  const canCreateFinancials = ACTIVE_JOB_STATUSES.has(effectiveStatus);
  const canCancel = ACTIVE_JOB_STATUSES.has(effectiveStatus);
  const canArchive = ARCHIVABLE_JOB_STATUSES.has(effectiveStatus);
  const isAnyLoading = actionLoading !== null;
  const canCreateEstimates = useHasPermission('estimates:create');
  const createApprovalEstimate = useCreateJobApprovalEstimate(id);
  const canCreateApprovalEstimate =
    !estimateId &&
    bookingType !== 'RECURRING' &&
    canCreateEstimates &&
    ['DRAFT', 'SCHEDULED', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED'].includes(effectiveStatus);

  const lifecycleMutation = useApiMutation<Job, { action: string; body?: Record<string, unknown> }>(
    {
      mutationFn: ({ action, body }) => {
        const endpointMap: Record<string, string> = {
          start: jobEndpoints.start(id),
          hold: jobEndpoints.hold(id),
          complete: jobEndpoints.complete(id),
          cancel: jobEndpoints.cancel(id),
          archive: jobEndpoints.archive(id),
          invoice: jobEndpoints.invoice(id),
          close: jobEndpoints.close(id),
        };
        return apiPost<Job>(operationsClient, endpointMap[action], body);
      },
      onSuccess: (_data, { action }) => {
        void queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
        void queryClient.invalidateQueries({ queryKey: jobKeys.lists() });
        const labels: Record<string, string> = {
          start: 'Job started',
          hold: 'Job put on hold',
          complete: 'Job completed',
          cancel: 'Job cancelled',
          archive: 'Job archived',
          invoice: 'Invoice generated',
          close: 'Job closed',
        };
        toast.success(labels[action] ?? 'Action completed');
        setActionLoading(null);
      },
      onError: (err) => {
        setActionLoading(null);
        const msg =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          (err instanceof Error ? err.message : null) ??
          'Action failed';
        toast.error(msg);
      },
    },
  );

  function trigger(action: string) {
    if (action === 'start' && !hasJobLineItems({ lineItems })) {
      toast.error(START_JOB_REQUIRES_LINE_ITEMS_MESSAGE);
      return;
    }
    setActionLoading(action);
    lifecycleMutation.mutate({ action });
  }

  function goToJob(search?: { tab?: 'visits' | 'finances' | 'notes' }) {
    void router.navigate({
      to: '/dashboard/jobs/$id',
      params: { id },
      search,
    });
  }

  if (isReadOnly) return null;

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <RowOverflowMenuTrigger title="Job actions" disabled={isAnyLoading} />
        </DropdownMenuTrigger>
        <OverflowMenuContent>
          {/* Status actions */}
          {canTransition && (
            <>
              {effectiveStatus === 'DRAFT' && canCreateVisit && (
                <DropdownMenuItem
                  onSelect={() =>
                    void router.navigate({
                      to: '/dashboard/jobs/$id/appointments/new',
                      params: { id },
                    })
                  }
                >
                  <CalendarDays className="mr-2 size-3.5" />
                  Schedule Visit
                </DropdownMenuItem>
              )}
              {(effectiveStatus === 'SCHEDULED' || effectiveStatus === 'ON_HOLD') && (
                <DropdownMenuItem
                  disabled={!hasJobLineItems({ lineItems })}
                  title={
                    !hasJobLineItems({ lineItems })
                      ? START_JOB_REQUIRES_LINE_ITEMS_MESSAGE
                      : undefined
                  }
                  onSelect={() => trigger('start')}
                >
                  <Play className="mr-2 size-3.5" />
                  {effectiveStatus === 'ON_HOLD' ? 'Resume' : 'Start'}
                </DropdownMenuItem>
              )}
              {(effectiveStatus === 'SCHEDULED' || effectiveStatus === 'IN_PROGRESS') &&
                jobEditScope === 'ALL' && (
                  <DropdownMenuItem onSelect={() => trigger('hold')}>
                    <Pause className="mr-2 size-3.5" />
                    Hold
                  </DropdownMenuItem>
                )}
              {effectiveStatus === 'IN_PROGRESS' && (
                <DropdownMenuItem onSelect={() => trigger('complete')}>
                  <CheckCircle2 className="mr-2 size-3.5" />
                  Complete
                </DropdownMenuItem>
              )}
              {effectiveStatus === 'COMPLETED' && (
                <DropdownMenuItem onSelect={() => trigger('invoice')}>
                  <FileText className="mr-2 size-3.5" />
                  Mark Invoiced
                </DropdownMenuItem>
              )}
              {effectiveStatus === 'INVOICED' && (
                <DropdownMenuItem onSelect={() => trigger('close')}>
                  <Lock className="mr-2 size-3.5" />
                  Close
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
            </>
          )}

          {/* Job actions */}
          {(canEditJob || canCreateVisit || canCreateApprovalEstimate) && (
            <>
              {canEditJob && (
                <DropdownMenuItem
                  onSelect={() =>
                    void router.navigate({ to: '/dashboard/jobs/$id/edit', params: { id } })
                  }
                >
                  <Pencil className="mr-2 size-3.5" />
                  Edit Job
                </DropdownMenuItem>
              )}
              {canCreateVisit && effectiveStatus !== 'DRAFT' && (
                <DropdownMenuItem
                  onSelect={() =>
                    void router.navigate({
                      to: '/dashboard/jobs/$id/appointments/new',
                      params: { id },
                    })
                  }
                >
                  <CalendarDays className="mr-2 size-3.5" />
                  New Visit
                </DropdownMenuItem>
              )}
              {canCreateApprovalEstimate && (
                <DropdownMenuItem
                  disabled={createApprovalEstimate.isPending}
                  onSelect={() =>
                    createApprovalEstimate.mutate(undefined, {
                      onSuccess: (result) => {
                        void router.navigate({
                          to: '/dashboard/estimates/$id',
                          params: { id: result.estimateId },
                          search: DEFAULT_ESTIMATE_DETAIL_SEARCH,
                        });
                      },
                    })
                  }
                >
                  {createApprovalEstimate.isPending ? (
                    <Loader2 className="mr-2 size-3.5 animate-spin" />
                  ) : (
                    <FilePlus2 className="mr-2 size-3.5" />
                  )}
                  Create Estimate
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
            </>
          )}

          {/* Financials */}
          {canCreateFinancials && (
            <>
              <DropdownMenuItem
                onSelect={() =>
                  void router.navigate({
                    to: '/dashboard/jobs/$id/change-orders/new',
                    params: { id },
                  })
                }
              >
                <FilePlus2 className="mr-2 size-3.5" />
                New Change Order
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() =>
                  void router.navigate({
                    to: '/dashboard/jobs/$id/invoices/new',
                    params: { id },
                  })
                }
              >
                <Receipt className="mr-2 size-3.5" />
                New Invoice
              </DropdownMenuItem>
              <DropdownMenuItem
                onSelect={() =>
                  void router.navigate({
                    to: '/dashboard/jobs/$id',
                    params: { id },
                    search: { tab: 'costs', expenseDrawer: 'new' },
                  })
                }
              >
                <BadgeDollarSign className="mr-2 size-3.5" />
                New Expense
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => goToJob({ tab: 'finances' })}>
                <CalendarDays className="mr-2 size-3.5" />
                Payment Schedule
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          )}

          {/* Links */}
          <DropdownMenuItem onSelect={() => goToJob({ tab: 'notes' })}>
            <MessageSquareText className="mr-2 size-3.5" />
            Notes
          </DropdownMenuItem>
          {estimateId && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={() =>
                  void router.navigate({
                    to: '/dashboard/estimates/$id',
                    params: { id: estimateId },
                    search: DEFAULT_ESTIMATE_DETAIL_SEARCH,
                  })
                }
              >
                <LinkIcon className="mr-2 size-3.5" />
                View Estimate
              </DropdownMenuItem>
            </>
          )}

          {/* Danger */}
          {(canCancel || canArchive) && (
            <>
              <DropdownMenuSeparator />
              {canCancel && (
                <DropdownMenuItem
                  onSelect={() => trigger('cancel')}
                  className="text-destructive focus:text-destructive"
                >
                  <XCircle className="mr-2 size-3.5" />
                  Cancel Job
                </DropdownMenuItem>
              )}
              {canArchive && (
                <DropdownMenuItem onSelect={() => trigger('archive')}>
                  <Archive className="mr-2 size-3.5" />
                  Archive Job
                </DropdownMenuItem>
              )}
            </>
          )}
        </OverflowMenuContent>
      </DropdownMenu>
    </>
  );
}
